import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaEdit, FaCheck, FaTimes, FaEnvelope, FaPhone, FaCalendar } from 'react-icons/fa';
import LuxuryButton from '../ui/LuxuryButton';
import LuxuryInput from '../ui/LuxuryInput';
import { cardFloat } from "../animations/animations";

const ProfileCard = ({ user, onUpdate, loading = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phoneNumber: user?.phoneNumber || '',
        dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : ''
    });
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (formData.phoneNumber && !/^[\+]?[0-9\s\-\(\)]{10,}$/.test(formData.phoneNumber)) {
            newErrors.phoneNumber = 'Phone number is invalid';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            await onUpdate(formData);
            setIsEditing(false);
            setErrors({});
        } catch (err) {
            console.error('Update failed:', err);
        }
    };

    const handleCancel = () => {
        setFormData({
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            email: user?.email || '',
            phoneNumber: user?.phoneNumber || '',
            dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toISOString().split('T')[0] : ''
        });
        setErrors({});
        setIsEditing(false);
    };

    return (
        <motion.div
            variants={cardFloat}
            className="luxury-card p-6"
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                        <FaUser className="w-5 h-5 text-champagne" />
                    </div>
                    <div>
                        <h3 className="font-heading text-xl font-medium text-charcoal">Personal Details</h3>
                        <p className="text-sm text-charcoal/50">Your account information</p>
                    </div>
                </div>

                {!isEditing && !loading && (
                    <LuxuryButton
                        variant="outline"
                        size="small"
                        onClick={() => setIsEditing(true)}
                        icon={FaEdit}
                        iconPosition="left"
                    >
                        Edit
                    </LuxuryButton>
                )}
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="space-y-4">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="animate-pulse">
                            <div className="h-4 bg-nude/20 rounded w-24 mb-2"></div>
                            <div className="h-6 bg-nude/20 rounded w-full"></div>
                        </div>
                    ))}
                </div>
            ) : (
                <AnimatePresence mode="wait">
                    <motion.div
                        key={isEditing ? 'edit' : 'view'}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                    >
                        {isEditing ? (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <LuxuryInput
                                            label="First Name"
                                            placeholder="Enter First Name"
                                            value={formData.firstName}
                                            onChange={(e) => {
                                                setFormData({...formData, firstName: e.target.value});
                                                if (errors.firstName) setErrors({...errors, firstName: null});
                                            }}
                                            error={errors.firstName}
                                            required
                                            size="medium"
                                        />
                                    </div>

                                    <div>
                                        <LuxuryInput
                                            label="Last Name"
                                            placeholder="Enter Last Name"
                                            value={formData.lastName}
                                            onChange={(e) => {
                                                setFormData({...formData, lastName: e.target.value});
                                                if (errors.lastName) setErrors({...errors, lastName: null});
                                            }}
                                            error={errors.lastName}
                                            required
                                            size="medium"
                                        />
                                    </div>

                                    <div className="md:col-span-2">
                                        <LuxuryInput
                                            type="email"
                                            label="Email Address"
                                            placeholder="Enter email address"
                                            value={formData.email}
                                            onChange={(e) => {
                                                setFormData({...formData, email: e.target.value});
                                                if (errors.email) setErrors({...errors, email: null});
                                            }}
                                            error={errors.email}
                                            required
                                            size="medium"
                                            icon={FaEnvelope}
                                        />
                                    </div>

                                    <div>
                                        <LuxuryInput
                                            type="tel"
                                            label="Phone Number"
                                            placeholder="Enter phone number"
                                            value={formData.phoneNumber}
                                            onChange={(e) => {
                                                setFormData({...formData, phoneNumber: e.target.value});
                                                if (errors.phoneNumber) setErrors({...errors, phoneNumber: null});
                                            }}
                                            error={errors.phoneNumber}
                                            size="medium"
                                            icon={FaPhone}
                                        />
                                    </div>

                                    <div>
                                        <LuxuryInput
                                            type="date"
                                            label="Date of Birth"
                                            placeholder="Enter Date of Birth"
                                            value={formData.dateOfBirth}
                                            onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                                            size="medium"
                                            icon={FaCalendar}
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-2 pt-4">
                                    <LuxuryButton
                                        variant="secondary"
                                        size="small"
                                        onClick={handleCancel}
                                        icon={FaTimes}
                                        iconPosition="left"
                                        type="button"
                                    >
                                        Cancel
                                    </LuxuryButton>
                                    <LuxuryButton
                                        variant="primary"
                                        size="small"
                                        type="submit"
                                        icon={FaCheck}
                                        iconPosition="left"
                                    >
                                        Save Changes
                                    </LuxuryButton>
                                </div>
                            </form>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <p className="text-sm text-charcoal/50 mb-1">Name</p>
                                    <p className="text-charcoal font-medium">
                                        {user?.firstName} {user?.lastName}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-sm text-charcoal/50 mb-1">Email</p>
                                    <p className="text-charcoal font-medium">{user?.email}</p>
                                </div>

                                <div>
                                    <p className="text-sm text-charcoal/50 mb-1">Phone</p>
                                    <p className="text-charcoal font-medium">
                                        {user?.phoneNumber || 'Not provided'}
                                    </p>
                                </div>

                                <div>
                                    <p className="text-sm text-charcoal/50 mb-1">Date of Birth</p>
                                    <p className="text-charcoal font-medium">
                                        {user?.dateOfBirth
                                            ? new Date(user.dateOfBirth).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })
                                            : 'Not provided'
                                        }
                                    </p>
                                </div>

                                <div className="md:col-span-2">
                                    <p className="text-sm text-charcoal/50 mb-1">Member Since</p>
                                    <p className="text-charcoal font-medium">
                                        {user?.createdAt
                                            ? new Date(user.createdAt).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })
                                            : 'Recently'
                                        }
                                    </p>
                                </div>
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            )}
        </motion.div>
    );
};

export default ProfileCard;